import React from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { Check, Route, Phone, FileText, Banknote, Clock, ShieldCheck } from 'lucide-react';
import { FinalContact } from '../components/FinalContact';
const FAST_ANSWERS = ['No forced dispatch', 'U.S.-based dispatch team', 'Load planning, not just load booking', 'Broker calls, rate cons, paperwork, and follow-up', 'Own authority and lease-on paths available'];
const HANDLES = [{
  icon: Route,
  title: 'Load planning'
}, {
  icon: Phone,
  title: 'Broker calls and follow-up'
}, {
  icon: FileText,
  title: 'Rate cons and paperwork'
}, {
  icon: Banknote,
  title: 'Billing support'
}, {
  icon: Clock,
  title: 'Check calls and updates'
}, {
  icon: ShieldCheck,
  title: 'Broker setup/verification handoffs'
}];
const FAQ = [{
  q: 'Do you force dispatch?',
  a: 'No. FSG presents load options and you choose which loads you accept.'
}, {
  q: 'Are dispatchers U.S.-based?',
  a: 'Yes. FSG uses a U.S.-based team with no foreign call centers.'
}, {
  q: 'What does FSG handle besides booking loads?',
  a: 'Load planning, broker communication, rate cons, paperwork, billing support, check calls, and follow-up.'
}, {
  q: 'Do I need my own authority?',
  a: 'No. You can run on your own authority or ask about leasing on with FSG.'
}, {
  q: 'How do I start?',
  a: 'Choose your setup and sign up, or call 234-602-8424.'
}];
export function DispatchServices() {
  useSeo({
    title: 'Truck Dispatch Services for Owner-Operators | FSG Transport',
    description: 'FSG Transport offers U.S.-based truck dispatch services for owner-operators and small fleets: load planning, broker calls, rate cons, paperwork, billing, and follow-up — with no forced dispatch.',
    path: '/dispatch-services'
  });
  return <div className="w-full" data-id="element-425">
      <section className="relative bg-navy text-white pt-36 pb-20 lg:pt-44 lg:pb-28 overflow-hidden" data-id="element-426">
        <div className="absolute inset-0 z-0 opacity-30" data-id="element-427">
          <img src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https%3A//assets.cdn.filesafe.space/cMkAnz3QfJpQ1Lp2u0QG/media/69160e581547ed5536c65e53.jpg" alt="Truck on the highway" className="w-full h-full object-cover" data-id="element-428" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-transparent" data-id="element-429" />
        </div>
        <div className="container-custom relative z-10 grid lg:grid-cols-12 gap-10 items-center" data-id="element-430">
          <div className="lg:col-span-7" data-id="element-431">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.1] mb-6 tracking-tight font-['Barlow']" data-id="element-432">
              Truck Dispatch Services for Owner-Operators Tired of Chasing Loads
              After Driving All Day
            </h1>
            <p className="text-lg text-off-white/80 font-medium leading-relaxed max-w-2xl" data-id="element-433">
              FSG helps owner-operators and small fleets find freight options,
              plan the week, talk to brokers, handle rate cons, organize
              paperwork, and keep loads moving — without forced dispatch.
            </p>
          </div>
          <div className="lg:col-span-5" data-id="element-434">
            <div className="bg-black/50 border border-white/10 rounded-sm p-8" data-id="element-435">
              <h2 className="text-sm font-bold uppercase tracking-widest text-amber mb-5" data-id="element-436">
                Fast Answers
              </h2>
              <ul className="space-y-4" data-id="element-437">
                {FAST_ANSWERS.map((a, i) => <li key={i} className="flex items-start gap-3" data-id="element-438">
                    <Check className="w-5 h-5 text-amber shrink-0 mt-0.5" data-id="element-439" />
                    <span className="font-semibold text-off-white/90" data-id="element-440">{a}</span>
                  </li>)}
              </ul>
              <Link to="/apply-now" className="btn-primary w-full mt-8" data-id="element-441">
                Start Dispatch
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-off-white" data-id="element-442">
        <div className="container-custom" data-id="element-443">
          <h2 className="text-3xl font-black text-navy mb-10 tracking-tight" data-id="element-444">
            What FSG Handles
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-445">
            {HANDLES.map((item, i) => <div key={i} className="bg-white border border-steel p-6 rounded-sm flex items-center gap-4" data-id="element-446">
                <item.icon className="w-6 h-6 text-red shrink-0" data-id="element-447" />
                <span className="font-bold text-navy" data-id="element-448">{item.title}</span>
              </div>)}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white border-t border-steel" data-id="element-449">
        <div className="container-custom grid md:grid-cols-3 gap-10" data-id="element-450">
          <div data-id="element-451">
            <h3 className="text-xl font-black text-navy mb-4 font-['Barlow']" data-id="element-452">
              No Forced Dispatch
            </h3>
            <p className="text-body font-medium leading-relaxed" data-id="element-453">
              FSG presents load options. The carrier decides what makes sense
              for the truck, equipment, lane, and schedule.
            </p>
          </div>
          <div data-id="element-454">
            <h3 className="text-xl font-black text-navy mb-4 font-['Barlow']" data-id="element-455">
              Load Planning Beyond the Next Load
            </h3>
            <p className="text-body font-medium leading-relaxed" data-id="element-456">
              FSG looks at how loads connect through the week, not just the next
              single load.
            </p>
          </div>
          <div data-id="element-457">
            <h3 className="text-xl font-black text-navy mb-4 font-['Barlow']" data-id="element-458">
              Broker Communication & Rate Cons
            </h3>
            <p className="text-body font-medium leading-relaxed" data-id="element-459">
              FSG helps reduce the time drivers spend calling, chasing, and
              sorting broker details after driving.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-off-white border-t border-steel" data-id="element-460">
        <div className="container-custom max-w-3xl" data-id="element-461">
          <h2 className="text-3xl font-black text-navy mb-8 tracking-tight" data-id="element-462">
            Dispatch FAQ
          </h2>
          <div className="space-y-5" data-id="element-463">
            {FAQ.map((f, i) => <div key={i} className="border-l-2 border-red pl-5" data-id="element-464">
                <h3 className="font-bold text-navy mb-1" data-id="element-465">{f.q}</h3>
                <p className="text-body font-medium leading-relaxed" data-id="element-466">{f.a}</p>
              </div>)}
          </div>
        </div>
      </section>

      <FinalContact data-id="element-467" />
    </div>;
}